import React, { useState, useRef, useEffect, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { HitmaxHero } from "../components/HitmaxHero";
import { HitmaxWordmark } from "../components/HitmaxWordmark";
import { CompetitionsMarquee } from "../components/CompetitionsMarquee";
import { HeroSection } from "../components/HeroSection";
import { HitmaxAuth } from "../components/HitmaxAuth";

const SportsBalls3D = lazy(() => import("../components/SportsBalls3D"));
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2 } from "lucide-react";
import { formatApiError } from "../lib/perf";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 43.5c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 34.5 26.7 35.5 24 35.5c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 36 43.5 30.6 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

export default function Login() {
  const { t } = useTranslation();
  const { login, register, loginWithGoogleSession } = useAuth();
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);
  const [partnerCode, setPartnerCode] = useState("");
  const cardRef = useRef(null);
  const logoRef = useRef(null);
  // Intro plays once per browser session (?intro=1 forces a replay for testing).
  const forceIntro = (() => { try { return new URLSearchParams(window.location.search).get("intro") === "1"; } catch (e) { return false; } })();
  const seenIntro = forceIntro ? false : (() => { try { return !!sessionStorage.getItem("hitmax_splash_seen"); } catch (e) { return false; } })();
  const [phase, setPhase] = useState(seenIntro ? "done" : "hero");
  const [landingVisible, setLandingVisible] = useState(seenIntro);

  // Handle return from Emergent Google Auth (session_id may arrive pre-render or slightly after in the preview iframe)
  useEffect(() => {
    let consumed = false;
    const tryConsume = () => {
      if (consumed) return true;
      let sid = window.__HITMAX_GSID;
      if (!sid) {
        const m = (window.location.hash || "").match(/session_id=([^&]+)/);
        if (m) sid = decodeURIComponent(m[1]);
      }
      if (!sid) return false;
      consumed = true;
      try { delete window.__HITMAX_GSID; } catch (e) {}
      try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch (e) {}
      setPhase("done");
      setLandingVisible(true);
      (async () => {
        try {
          await loginWithGoogleSession(sid);
          toast.success(t("common.welcome_toast"));
        } catch (err) {
          toast.error(t("auth.google_failed"));
        }
      })();
      return true;
    };
    if (tryConsume()) return;
    const onHash = () => tryConsume();
    window.addEventListener("hashchange", onHash);
    let n = 0;
    const iv = window.setInterval(() => { if (tryConsume() || ++n > 20) window.clearInterval(iv); }, 150);
    return () => { window.removeEventListener("hashchange", onHash); window.clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // 18 ans et acceptation des CGU / de la confidentialité (obligatoires à l'inscription).
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [notice, setNotice] = useState("");
  const resendActivation = async () => {
    try { await api.post("/auth/resend-verification-public", { email }); } catch {}
    setNotice(t("auth.activation_resent"));
  };

  // Une seule validation à la fois (double appui, touche « OK » du clavier iOS + bouton).
  const submitting = useRef(false);
  const submit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true); setError("");
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        setSent(true);
      } else if (mode === "login") {
        try {
          await login(email, password);
        } catch (err) {
          // Administrateur : double authentification (audit sécurité C15).
          if (err.response?.data?.detail !== "mfa_required") throw err;
          const code = window.prompt(t("auth.mfa_prompt"));
          if (!code) { setLoading(false); return; }
          await login(email, password, code.trim());
        }
        toast.success(t("common.welcome_toast"));
      } else {
        if (password.length < 6) { setError(t("reset.min_len")); setLoading(false); return; }
        if (!acceptTerms) { setError(t("auth.terms_required")); setLoading(false); return; }
        const res = await register(`${firstName} ${name}`.trim(), email, password, partnerCode.trim() || undefined, true);
        if (res?.pending) {
          // C16 : compte à activer depuis l'e-mail ; on bascule sur la connexion.
          setPassword("");
          setMode("login");
          setNotice(t("auth.check_email_activate"));
          setLoading(false);
          return;
        }
        toast.success(t("common.welcome_toast"));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      if (err.response?.status === 403 && mode === "login") setNotice(t("auth.activation_hint"));
    } finally {
      submitting.current = false;
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen">
      {phase === "hero" && (
        <HeroSection
          isActive
          targetRef={logoRef}
          onReveal={() => setLandingVisible(true)}
          onFinish={() => {
            try { sessionStorage.setItem("hitmax_splash_seen", "1"); } catch (e) {}
            setLandingVisible(true);
            setPhase("done");
          }}
        />
      )}
      {phase !== "done" && (
        <div
          data-testid="skip-intro"
          onClick={() => {
            try { sessionStorage.setItem("hitmax_splash_seen", "1"); } catch (e) {}
            setPhase("done");
          }}
          className="hidden"
        />
      )}
      <div
        className="min-h-screen"
        style={{
          opacity: landingVisible ? 1 : 0,
          transform: landingVisible ? "scale(1)" : "scale(1.06)",
          transformOrigin: "50% 40%",
          filter: landingVisible ? "blur(0px)" : "blur(6px)",
          transition: "opacity 800ms ease, transform 900ms cubic-bezier(0.22,0.61,0.36,1), filter 800ms ease",
        }}
      >
        <HitmaxAuth
          mode={mode} setMode={setMode}
          firstName={firstName} setFirstName={setFirstName}
          name={name} setName={setName}
          email={email} setEmail={setEmail}
          password={password} setPassword={setPassword}
          loading={loading} error={error}
          forgot={forgot} setForgot={setForgot}
          sent={sent} setSent={setSent}
          partnerCode={partnerCode} setPartnerCode={setPartnerCode}
          acceptTerms={acceptTerms} setAcceptTerms={setAcceptTerms}
          notice={notice} resendActivation={resendActivation}
          setError={setError}
          submit={submit} handleGoogle={handleGoogle}
          GoogleIcon={GoogleIcon}
        />
        <div style={{ background: "#070A08", borderTop: "1px solid rgba(255,255,255,0.08)" }} className="w-full flex justify-center px-6 py-3">
          <div className="w-full max-w-2xl">
            <CompetitionsMarquee />
          </div>
        </div>
      </div>
    </div>
  );
}
